// src/stores/module.ts
import { defineStore } from "pinia";
import { nextTick } from "vue";
import RawModuleFactory from "@/library/modules/moduleFactory";
import { useContentStore } from "./content";
import { restoreAllUserComponents } from "@/library/userModuleRegistry";

import {
  addUserModuleTemplate,
  deleteUserModuleTemplate,
  renameUserModuleTemplate,
  deleteUserComponent as registryDeleteUserComponent,
  renameUserComponent as registryRenameUserComponent,
  loadUserModuleTemplates
} from "@/library/userModuleRegistry";

import type { UserPresetTemplate } from "@/types/userPresetTemplate";
import { createUserTemplateCreator } from "@/library/modules/userTemplateCreator";

export const useMoudleStore = defineStore("module", {
  state: () => ({
    moduleFactory: [...(RawModuleFactory as any[])],
    selectedModuleIdx: 0,

   
    creatorById: new Map<string, any>()
  }),

  getters: {
    selectedModuleCreator(state) {
      return state.moduleFactory[state.selectedModuleIdx];
    }
  },

  actions: {
   
    registerUserCreatorInMap(creator: any) {
      if (!creator) return;

      // user preset template
      if (creator.isUserTemplate && creator.templateId) {
        this.creatorById.set(`template:${creator.templateId}`, creator);
      }

      // user component
      if (creator.isUserComponent) {
        const cid =
          creator.componentId ??
          creator.componentJson?.id ??
          creator.componentJson?.name ??
          creator.moduleName;
        if (cid) {
          this.creatorById.set(`component:${cid}`, creator);
        }
      }
    },

    
    getCreatorForModuleOutput(m: any) {
      if (!m) return null;

      // user preset template
      if (m.templateId) {
        const c = this.creatorById.get(`template:${m.templateId}`);
        if (c) return c;
      }

      // user component
      if (m.componentId) {
        const c = this.creatorById.get(`component:${m.componentId}`);
        if (c) return c;
      }

      
      return this.moduleFactory?.[m.moduleIdx] ?? null;
    },

    selectModule(idx: number) {
      console.log("[moduleStore] selectModule", idx);
      this.selectedModuleIdx = idx;

      const creator: any = this.selectedModuleCreator;
      if (creator && typeof creator.resetModule === "function") {
        creator.resetModule();
      }

      const contentStore = useContentStore() as any;
      if (contentStore && typeof contentStore.clearSelected === "function") {
        contentStore.clearSelected();
      }
    },

    
    addUserCreator(creator: any) {
      console.log("[moduleStore] addUserCreator", creator?.moduleName);

      const initializeAt = (index: number) => {
        const storedCreator: any = this.moduleFactory[index];
        this.registerUserCreatorInMap(storedCreator);

        // Let the newly inserted card mount first. Updating pendingModule in a
        // separate Vue flush makes the replacement observable on first import.
        void nextTick(() => {
          if (
            this.moduleFactory[index] === storedCreator &&
            storedCreator?.isUserComponent &&
            typeof storedCreator.resetModule === "function"
          ) {
            storedCreator.resetModule();
          }
        });
        return index;
      };

      if (creator?.isUserComponent) {
        const componentId =
          creator.componentId ??
          creator.componentJson?.id ??
          creator.componentJson?.name ??
          creator.moduleName ??
          creator.displayName;

        const sameUserComponent = (c: any) => {
          if (!c?.isUserComponent) return false;
          const candidateId =
            c.componentId ??
            c.componentJson?.id ??
            c.componentJson?.name ??
            c.moduleName ??
            c.displayName;
          return !!componentId && candidateId === componentId;
        };

        const existingIndex = this.moduleFactory.findIndex(sameUserComponent);
        if (existingIndex >= 0) {
          this.moduleFactory = this.moduleFactory.filter((c: any, idx: number) => {
            return idx === existingIndex || !sameUserComponent(c);
          });
          this.moduleFactory[existingIndex] = creator;
          return initializeAt(existingIndex);
        }
      }

      this.moduleFactory.push(creator);
      return initializeAt(this.moduleFactory.length - 1);
    },

    createUserTemplate(template: UserPresetTemplate) {
      const baseCreator: any = this.selectedModuleCreator;
      if (!baseCreator) {
        console.error("[moduleStore] createUserTemplate: no baseCreator");
        return;
      }

      addUserModuleTemplate(template);

      const wrapperCreator = createUserTemplateCreator(template, baseCreator);
      if (!wrapperCreator) {
        console.error("[moduleStore] createUserTemplate: failed to create wrapperCreator");
        return;
      }

      this.moduleFactory.push(wrapperCreator);
      this.registerUserCreatorInMap(wrapperCreator);
    },

    createUserTemplateFromPending(name: string) {
      const baseCreator: any = this.selectedModuleCreator;
      if (!baseCreator?.pendingModule) {
        console.error("[moduleStore] createUserTemplateFromPending: no pendingModule");
        return;
      }

      const template: UserPresetTemplate = {
        id: `tpl_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: name.trim(),
        baseModuleIdx:
          typeof baseCreator.getModuleIndex === "number"
            ? baseCreator.getModuleIndex
            : typeof baseCreator.pendingModule?.getModuleIndex === "number"
              ? baseCreator.pendingModule.getModuleIndex
              : 0,
        properties: JSON.parse(JSON.stringify(baseCreator.pendingModule.properties))
      };

      this.createUserTemplate(template);
    },

    renameUserTemplate(templateId: string, newName: string) {
      console.log("[moduleStore] renameUserTemplate", templateId, newName);
      renameUserModuleTemplate(templateId, newName);

      this.moduleFactory.forEach((creator: any) => {
        if (creator?.isUserTemplate && creator?.templateId === templateId) {
          if ("displayName" in creator) creator.displayName = newName;
          if ("moduleName" in creator) creator.moduleName = newName;
        }
      });
    },

    deleteUserTemplate(templateId: string) {
      console.log("[moduleStore] deleteUserTemplate", templateId);

      deleteUserModuleTemplate(templateId);
      this.creatorById.delete(`template:${templateId}`);

      this.moduleFactory = this.moduleFactory.filter((creator: any) => {
        return !(creator?.isUserTemplate && creator?.templateId === templateId);
      });

      if (this.selectedModuleIdx >= this.moduleFactory.length) {
        this.selectedModuleIdx = Math.max(0, this.moduleFactory.length - 1);
      }
    },

    renameUserComponent(oldName: string, newName: string) {
      console.log("[moduleStore] renameUserComponent", oldName, "->", newName);

      registryRenameUserComponent(oldName, newName);

      const creator: any = this.moduleFactory.find(
        (c: any) =>
          c?.isUserComponent &&
          (c.moduleName === oldName || c.displayName === oldName)
      );

      if (creator) {
        if ("moduleName" in creator) creator.moduleName = newName;
        if ("displayName" in creator) creator.displayName = newName;

      
      } else {
        console.warn("[moduleStore] renameUserComponent: creator not found for", oldName);
      }
    },

    initiallizeModuleFactory() {
  console.log("[moduleStore] initiallizeModuleFactory");

  
  this.moduleFactory = [...(RawModuleFactory as any[])];
  this.selectedModuleIdx = 0;

  
  this.creatorById = new Map<string, any>();

  
  restoreAllUserComponents();

  
  const templates = loadUserModuleTemplates();
  templates.sort((a, b) => String(a.id).localeCompare(String(b.id)));

  for (const t of templates) {
   
    let baseCreator: any = this.moduleFactory[t.baseModuleIdx];

    
    if (!baseCreator) {
      baseCreator = this.moduleFactory.find((c: any) => {
        if (!c) return false;

        if (typeof c.getModuleIndex === "function") {
          return c.getModuleIndex() === t.baseModuleIdx;
        }
        if (typeof c.getModuleIndex === "number") {
          return c.getModuleIndex === t.baseModuleIdx;
        }
        if (c.pendingModule && typeof c.pendingModule.getModuleIndex === "number") {
          return c.pendingModule.getModuleIndex === t.baseModuleIdx;
        }
        return false;
      });
    }

    if (!baseCreator) {
      console.warn("[moduleStore] baseCreator not found for template", t);
      continue;
    }

    const wrapper = createUserTemplateCreator(t as any, baseCreator);
    if (wrapper) {
      this.moduleFactory.push(wrapper);
      this.registerUserCreatorInMap(wrapper);
    }
  }
},


    deleteUserComponent(name: string) {
      console.log("[moduleStore] deleteUserComponent", name);

      registryDeleteUserComponent(name);

      this.moduleFactory = this.moduleFactory.filter((c: any) => {
        return !(c?.isUserComponent && (c.moduleName === name || c.displayName === name));
      });

      
      this.creatorById.delete(`component:${name}`);

      if (this.selectedModuleIdx >= this.moduleFactory.length) {
        this.selectedModuleIdx = Math.max(0, this.moduleFactory.length - 1);
      }
    }
  }
});
